'use client';

import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import {cn} from '@common-web/lib/utils';

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> & {noHash?: boolean}
>(({defaultValue, value, onValueChange, noHash, ...props}, ref) => {
  const [internalValue, setInternalValue] = React.useState(defaultValue);

  React.useEffect(() => {
    if (noHash) return;
    // Check for hash on mount and when hash changes
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1); // Remove the #
      if (hash && !value) {
        // Only set if not controlled
        setInternalValue(hash);
        onValueChange?.(hash);
      }
    };

    // Handle initial hash
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [value, onValueChange, noHash]);

  // Set hash on mount if there's an active tab but no hash
  React.useEffect(() => {
    if (noHash) return;
    const currentHash = window.location.hash.slice(1);
    if (!currentHash && effectiveValue) {
      window.history.replaceState(null, '', `#${effectiveValue}`);
    }
  }, []);

  // Use provided value if controlled, otherwise use internal value
  const effectiveValue = value !== undefined ? value : internalValue;

  return (
    <TabsPrimitive.Root
      ref={ref}
      value={effectiveValue}
      onValueChange={(newValue) => {
        if (!value) {
          // Only update internal state if not controlled
          setInternalValue(newValue);
        }
        onValueChange?.(newValue);
      }}
      {...props}
    />
  );
});
Tabs.displayName = TabsPrimitive.Root.displayName;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({className, ...props}, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      'inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground',
      className,
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {noHash?: boolean}
>(({noHash, className, onClick, ...props}, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    onClick={(e) => {
      if (onClick) onClick(e);
      // set the page hash to the tab value
      if (noHash) return;

      const value = props.value;
      if (value) {
        window.location.hash = value;
      }
    }}
    className={cn(
      'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow',
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({className, ...props}, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export {Tabs, TabsList, TabsTrigger, TabsContent};
